import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSettings } from '../../context/SettingsContext';
import { useI18n } from '../../context/I18nContext';
import {
  getInactiveDeletionDays,
  initInactiveDeletionConfig,
} from '../../services/serverConfigService';
import './AutoLogoutPage.css'; // reuse same form/button styles

const ChatExpiryReminderPage = ({ onBack }) => {
  const { settings, updateSetting } = useSettings();
  const { t } = useI18n();

  // Read the cached value at mount, then re-read once the silent fetch
  // resolves so users who open Settings before the prepared-sync fetch
  // completes still see the correct cap and description.
  const [serverDeletionDays, setServerDeletionDays] = useState(() => getInactiveDeletionDays());
  useEffect(() => {
    let active = true;
    initInactiveDeletionConfig()
      .then((days) => { if (active) setServerDeletionDays(days); })
      .catch(() => { /* service returns fallback; nothing to surface */ });
    return () => { active = false; };
  }, []);
  const MAX_REMINDER_DAYS = Math.max(0, serverDeletionDays - 1);

  const savedValueRef = useRef(settings.chatExpiryReminderDays ?? 7);
  const successTimeoutRef = useRef(null);
  const noResultsTimeoutRef = useRef(null);

  const [reminderDays, setReminderDays] = useState(() =>
    String(settings.chatExpiryReminderDays ?? 7),
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);

  // Sync with external settings changes (e.g., from another tab/device)
  useEffect(() => {
    const newSaved = settings.chatExpiryReminderDays ?? 7;
    savedValueRef.current = newSaved;
    setReminderDays(String(newSaved));
  }, [settings.chatExpiryReminderDays]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (noResultsTimeoutRef.current) {
        clearTimeout(noResultsTimeoutRef.current);
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    if (['-', '+', '.', 'e', 'E'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    if (value === '') {
      setReminderDays('');
      return;
    }
    const num = parseInt(value, 10);
    if (num < 0) return;
    setReminderDays(String(Math.min(num, MAX_REMINDER_DAYS)));
  };

  const handleSave = (e) => {
    e.preventDefault();
    const numValue = parseInt(reminderDays, 10) || 0;
    updateSetting('chatExpiryReminderDays', numValue);
    savedValueRef.current = numValue;

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    setShowSuccess(true);
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccess(false);
      successTimeoutRef.current = null;
    }, 3000);
  };

  const handleTestClick = () => {
    // Reset any previous no-results message, then trigger the forced check
    setShowNoResults(false);
    if (noResultsTimeoutRef.current) {
      clearTimeout(noResultsTimeoutRef.current);
    }
    window.dispatchEvent(new CustomEvent('ti-m:trigger-expiry-check'));
  };

  // Show feedback when the forced check finds no expiring rooms
  useEffect(() => {
    const handler = () => {
      setShowNoResults(true);
      noResultsTimeoutRef.current = setTimeout(() => {
        setShowNoResults(false);
        noResultsTimeoutRef.current = null;
      }, 5000);
    };
    window.addEventListener('ti-m:expiry-check-empty', handler);
    return () => window.removeEventListener('ti-m:expiry-check-empty', handler);
  }, []);

  const handleBack = () => {
    setReminderDays(String(savedValueRef.current));
    onBack();
  };

  const currentValue = parseInt(reminderDays, 10) || 0;
  const hasChanges = currentValue !== savedValueRef.current;

  return (
    <div className="auto-logout-page">
      <div className="settings-modal-header">
        <button
          className="back-button"
          onClick={handleBack}
          aria-label={t('ChatExpiryReminderPage.goBackToSettings', {}, 'Go back to settings')}
        >
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="settings-page-title">
          {t('ChatExpiryReminderPage.title', {}, 'Chat Expiry Reminders')}
        </span>
      </div>
      <div className="auto-logout-content">
        <form className="auto-logout-form" onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="expiry-reminder-input" className="form-label">
              {t(
                'ChatExpiryReminderPage.description',
                { days: serverDeletionDays },
                `Warn you when a conversation is approaching the server's ${serverDeletionDays}-day inactivity deletion limit. Set to 0 to disable reminders.`,
              )}
            </label>
            <input
              id="expiry-reminder-input"
              type="number"
              min="0"
              max={MAX_REMINDER_DAYS}
              className="form-input"
              value={reminderDays}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              autoComplete="off"
            />
            {showSuccess && (
              <div
                className="success-message"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                ✓ {t('ChatExpiryReminderPage.settingsSaved', {}, 'Settings saved')}
              </div>
            )}
          </div>
          <div className="auto-logout-footer">
            <button
              type="button"
              className="settings-modal-button"
              onClick={handleTestClick}
            >
              {t('ChatExpiryReminderPage.testButton', {}, 'Test Reminder Now')}
            </button>
            <button
              type="submit"
              className="settings-modal-button primary"
              disabled={!hasChanges}
            >
              {t('ChatExpiryReminderPage.save', {}, 'Save')}
            </button>
          </div>
          {showNoResults && (
            <div
              className="success-message"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {t('ChatExpiryReminderPage.noExpiring', {}, 'No chats are currently expiring.')}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

ChatExpiryReminderPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default ChatExpiryReminderPage;
